/** Dashboard ortak model, kuyruk tanımları ve yardımcılar */
import { useMemo } from "react";
import {
    calcTrend,
    computeHealth,
    muvekkilAd,
    normalizeFileRow,
    normalizeNotifyItem,
    pickNum,
} from "./DashboardPro";

export const QUEUE_DEFS = [
    {
        id: "tahkim_gecmis",
        title: "Tahkime geçmesi gereken",
        short: "Tahkime geç",
        help: "Başvuru aşamasında 16+ gün geçmiş dosyalar. Acil tahkim başvurusu yapılmalı.",
        action: "Tahkim başvurusunu tamamlayın",
        tone: "rose",
        countKeys: ["bekleyenBasvuru16Gun", "tahkimeGidecek"],
        rowsKey: "tahkimeGidecekRows",
    },
    {
        id: "tahkim_yaklasan",
        title: "Tahkimi yaklaşan (12–15 gün)",
        short: "Yaklaşan",
        help: "Sadece başvuruda 12–15. gün arası dosyalar. Başvuruda (tümü) listesinden farklıdır.",
        action: "Evrak kontrolü ve tahkim hazırlığı",
        tone: "amber",
        countKeys: ["tahkimYaklasanBasvuru"],
        rowsKey: "tahkimYaklasanBasvuruRows",
        fromAdvanced: true,
    },
    {
        id: "tahkimde",
        title: "Tahkimde olanlar",
        short: "Tahkimde",
        help: "Tahkim sürecindeki aktif dosyalar.",
        action: "Tahkim Merkezi'nden yönetin",
        tone: "violet",
        countKeys: ["tahkim_bekleyen", "tahkimSurecSayisi"],
        rowsKey: "tahkimRows",
    },
    {
        id: "tahkim_sla",
        title: "Tahkim süresi yaklaşan",
        short: "SLA yakın",
        help: "Tahkim aşamasında SLA süresi dolmak üzere.",
        action: "Acil cevap / evrak tamamlama",
        tone: "orange",
        countKeys: ["tahkimYaklasanSayisi"],
        rowsKey: "tahkimYaklasanSlaRows",
    },
    {
        id: "basvuruda",
        title: "Başvuruda olanlar (tümü)",
        short: "Başvuruda",
        help: "Tahkime henüz geçmemiş tüm başvuru dosyaları. Yaklaşan ve normal başvurular birlikte listelenir.",
        action: "Başvuru evraklarını tamamlayın",
        tone: "sky",
        countKeys: ["basvurudaSayisi"],
        rowsKey: "basvurudaRows",
    },
    {
        id: "yeni_dosyalar",
        title: "Yeni dosyalar (7 gün)",
        short: "Yeni",
        help: "Son bir haftada açılan dosyalar.",
        action: "Müvekkili bilgilendirin",
        tone: "emerald",
        countKeys: ["yeniDosya7Gun"],
        rowsKey: "yeniDosyaRows",
        fromAdvanced: true,
    },
    {
        id: "yatar_dosyalar",
        title: "Yatar dosyalar",
        short: "Yatar",
        help: "Yatar konusu veya yatar açılacak işaretli dosyalar.",
        action: "Yatar sürecini başlatın",
        tone: "yellow",
        countKeys: ["yatarAcilacakSayisi"],
        rowsKey: "yatarRows",
    },
    {
        id: "zamanasimi_yaklasan",
        title: "Zamanaşımı yaklaşan dosyalar",
        short: "Zamanaşımı",
        help: "SLA / hak düşürücü süre son gün veya geçmiş dosyalar.",
        action: "Acil süre kontrolü",
        tone: "rose",
        countKeys: ["sla_alert_count"],
        rowsKey: "zamanasimiRows",
        fromAdvanced: true,
    },
    {
        id: "bekleyen_evraklar",
        title: "Onay bekleyen evraklar",
        short: "Evrak",
        help: "Müvekkil veya ofisten yüklenen, onay bekleyen evraklar.",
        action: "Evrak onayını tamamlayın",
        tone: "amber",
        countKeys: ["evrak"],
        rowsKey: "bekleyenEvrakRows",
        fromAdvanced: true,
    },
    {
        id: "randevu_bugun",
        title: "Bugünkü randevular",
        short: "Randevu",
        help: "Bugün planlanmış tüm randevular.",
        action: "Ajandayı kontrol edin",
        tone: "sky",
        countKeys: ["randevu"],
        rowsKey: "randevuBugunRows",
        fromAdvanced: true,
    },
    {
        id: "ai_yeni_leadler",
        title: "AI / online yeni lead'ler",
        short: "AI Lead",
        help: "Son 7 gün online başvuru ve AI kanalı lead kayıtları.",
        action: "Lead'leri arayın / dosya açın",
        tone: "emerald",
        countKeys: ["aiLeadSayisi"],
        rowsKey: "aiLeadRows",
        fromAdvanced: true,
    },
];

export const QUEUE_TONE = {
    rose: { dot: "bg-rose-500", badge: "bg-rose-500/20 text-rose-300", border: "border-rose-500/40" },
    amber: { dot: "bg-amber-500", badge: "bg-amber-500/20 text-amber-300", border: "border-amber-500/40" },
    violet: { dot: "bg-violet-500", badge: "bg-violet-500/20 text-violet-300", border: "border-violet-500/40" },
    orange: { dot: "bg-orange-500", badge: "bg-orange-500/20 text-orange-300", border: "border-orange-500/40" },
    sky: { dot: "bg-sky-500", badge: "bg-sky-500/20 text-sky-300", border: "border-sky-500/40" },
    emerald: { dot: "bg-emerald-500", badge: "bg-emerald-500/20 text-emerald-300", border: "border-emerald-500/40" },
    yellow: { dot: "bg-yellow-500", badge: "bg-yellow-500/20 text-yellow-300", border: "border-yellow-500/40" },
};

const COUNT_MAP_KEYS = {
    bekleyenBasvuru16Gun: "tahkimeGidecek",
    tahkim_bekleyen: "tahkim",
    tahkimSurecSayisi: "tahkim",
    basvurudaSayisi: "basvuruda",
    tahkimYaklasanBasvuru: "tahkimYaklasanBasvuru",
    tahkimYaklasanSayisi: "tahkimYaklasanSayisi",
    yatarAcilacakSayisi: "yatar",
    yeniDosya7Gun: "yeniDosya7Gun",
    tahkimeGidecek: "tahkimeGidecek",
};

export function initials(name) {
    const parts = (name || "").split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    return parts
        .slice(0, 2)
        .map((p) => p[0])
        .join("")
        .toUpperCase();
}

export function displayMuvekkil(item) {
    const ad = muvekkilAd(item);
    if (!ad || ad === "—") return { ad: "Müvekkil atanmamış", missing: true };
    return { ad, missing: false };
}

export function resolveQueue(def, model) {
    let rows = [];
    let count = 0;

    if (def.rowsKey) {
        rows = model[def.rowsKey] || [];
    }

    if (!rows.length && def.fromAdvanced && model.advMap[def.id]) {
        const g = model.advMap[def.id];
        rows = (g.items || []).map(normalizeNotifyItem).filter(Boolean);
        count = g.count ?? rows.length;
    }

    if (def.countKeys) {
        for (const k of def.countKeys) {
            const kpiKey = COUNT_MAP_KEYS[k] || k;
            const mapped = model.kpis[kpiKey];
            if (mapped != null && mapped !== undefined) {
                count = Math.max(count, mapped);
                break;
            }
        }
    }

    if (!count) count = rows.length;

    return { rows, count };
}

export function useDashboardModel(data) {
    return useMemo(() => {
        const prev = data?.previous || {};
        const kpis = {
            muvekkil: pickNum(data, ["muvekkil", "toplam_muvekkil"]),
            dosya: pickNum(data, ["dosya", "toplam_dosya"]),
            degerKaybi: pickNum(data, ["degerKaybi"]),
            araci: pickNum(data, ["araci"]),
            tahkim: pickNum(data, ["tahkim_bekleyen", "tahkimSurecSayisi"]),
            yatar: pickNum(data, ["yatarAcilacakSayisi"], pickNum(data?.yatar_dosyalar, ["yatar"])),
            tahkimeGidecek: pickNum(data, ["bekleyenBasvuru16Gun"]),
            basvuruda: pickNum(data, ["basvurudaSayisi"]),
            tahkimYaklasanSayisi: pickNum(data, ["tahkimYaklasanSayisi"]),
            tahkimYaklasanBasvuru: pickNum(data, ["tahkimYaklasanBasvuruSayisi"]),
            yeniDosya7Gun: pickNum(data, ["yeniDosya7Gun"]),
            evrak: pickNum(data, ["bekleyen_evrak"]),
            odeme: pickNum(data, ["bekleyen_odeme", "odeme_bekleyen"]),
            fatura: pickNum(data, ["fatura_bekleyen", "bekleyen_fatura"]),
            randevu: pickNum(data, ["randevu_bugun"]),
            hatirlatma: pickNum(data, ["hatirlatma_bugun", "aktif_hatirlatma"]),
            whatsapp: pickNum(data, ["whatsapp_bekleyen", "whatsappBekleyen"]),
            icra: pickNum(data, ["aktif_icralar"]),
            sla_alert_count: (data?.sla_alerts || []).length,
            aiLeadSayisi: pickNum(data, ["aiChatLeadSayisi"], (data?.advanced_notifications || []).find((g) => g.key === "ai_yeni_leadler")?.count || 0),
            yeniMuvekkil: pickNum(data, ["yeni_muvekkil_30"]),
            yeniDosya: pickNum(data, ["yeni_dosya_30"]),
            buAyMuvekkil: pickNum(data, ["buAyMuvekkil"]),
            buAyDosya: pickNum(data, ["buAySigortaDosya"]),
            tahsilatDonem: pickNum(
                data?.odemeOzet,
                ["odemeMiktariToplam"],
                pickNum(data?.tahsilat_toplam, ["tahsilat"]),
            ),
        };

        const advMap = {};
        (data?.advanced_notifications || []).forEach((g) => {
            advMap[g.key] = g;
            if (g.key === "tahkim_yaklasan") {
                kpis.tahkimYaklasanBasvuru = Math.max(
                    kpis.tahkimYaklasanBasvuru,
                    g.count ?? 0,
                );
            }
            if (g.key === "yeni_dosyalar" && !kpis.yeniDosya7Gun) kpis.yeniDosya7Gun = g.count ?? 0;
        });

        const trends = {
            muvekkil: calcTrend(kpis.muvekkil, pickNum(prev, ["muvekkil", "toplam_muvekkil"])),
            dosya: calcTrend(kpis.dosya, pickNum(prev, ["dosya", "toplam_dosya"])),
            tahkim: calcTrend(kpis.tahkim, pickNum(prev, ["tahkim_bekleyen"])),
            yatar: calcTrend(kpis.yatar, pickNum(prev?.yatar_dosyalar, ["yatar"])),
            yeniMuvekkil: calcTrend(kpis.yeniMuvekkil, pickNum(prev, ["yeni_muvekkil_30"])),
            yeniDosya: calcTrend(kpis.yeniDosya, pickNum(prev, ["yeni_dosya_30"])),
        };

        const mapRows = (arr) => (arr || []).slice(0, 50).map(normalizeFileRow).filter(Boolean);

        return {
            kpis,
            trends,
            health: computeHealth(kpis),
            advMap,
            chartData: (data?.chartData || []).map((d) => ({
                name: d?.name ?? "",
                dosya: Number(d?.dosya ?? 0) || 0,
                muvekkil: Number(d?.muvekkil ?? 0) || 0,
            })),
            agenda: (data?.bugunAjanda || []).slice(0, 8),
            odemeList: data?.bekleyen_odeme_listesi || data?.bekleyenOdemeListesi || [],
            tahkimeGidecekRows: mapRows(data?.tahkimeGidecekSatirlari),
            tahkimRows: mapRows(data?.tahkimDosyalari || data?.tahkimDosyaSatirlari),
            yatarRows: mapRows(data?.yatarDosyalari || data?.yatarDosyaSatirlari),
            basvurudaRows: mapRows(data?.basvurudaSatirlari),
            tahkimYaklasanBasvuruRows: mapRows(data?.tahkimYaklasanBasvuruSatirlari),
            tahkimYaklasanSlaRows: mapRows(data?.yaklasanTahkimSlaSatirlari),
            yeniDosyaRows: mapRows(
                data?.yeniDosya7GunSatirlari ||
                    (data?.advanced_notifications || [])
                        .find((g) => g.key === "yeni_dosyalar")
                        ?.items?.map((it) => ({
                            id: it.id,
                            dosyaNo: it.dosya_no || it.dosyaNo,
                            muvekkilAdSoyad: it.muvekkil_ad_soyad || it.muvekkilAdSoyad,
                            plaka: it.plaka,
                            tarih: it.tarih,
                        })),
            ),
            gecikmisTahkimRows: mapRows(data?.gecikmisTahkimSatirlari),
        };
    }, [data]);
}
